/**
 * The data a form prints from.
 *
 * The form's own Print builds it from what the filler already holds (`buildFormPrintData`); the
 * logs and the task board print forms they have not opened, so `fetchFormPrintData` loads the same
 * pieces in batches and runs them through the same builder. Either way the printed document gets
 * the sections hidden on lock, the patient's relationships (guardian/POA designations), the
 * signers' records and the lock time.
 */
import { $httpFhirApi } from '@/common/api/httpFhir.service';
import { getUrlFromCanonical } from '@/common/core';
import { fetchRelationshipsForPatients } from '@/common/roiAuthorizers';
import { unref } from 'vue';

// Ids per search request, so a long Print all stays under the URL length limit.
const ID_CHUNK_SIZE = 50;

/** The linkIds of each response's top-level sections hidden on lock, keyed by response id. */
export const hiddenSectionsByResponse = (qrs) => {
    if (!qrs) return {};
    const responses = Array.isArray(qrs) ? qrs : [qrs];
    return Object.fromEntries(responses.filter(Boolean).map((qr) => [qr.id, (qr.item || []).filter((item) => item.hideFieldSetSection === true).map((item) => item.linkId)]));
};

export const buildFormPrintData = ({
    questionnaire,
    questionnaireResponse,
    person,
    encounter,
    episodeOfCare,
    procedures,
    relationships,
    signerSources,
    lockTimestamp,
    groupItemFilter,
    snapshotMode,
}) => ({
    questionnaire,
    questionnaireResponse,
    hideFieldSetSectionInfo: hiddenSectionsByResponse(questionnaireResponse),
    person,
    encounter,
    episodeOfCare,
    procedures,
    relationships: (relationships || []).filter((r) => r?.resourceType === 'BN_Relationship'),
    signerSources: signerSources || [],
    lockTimestamp: unref(lockTimestamp) || null,
    groupItemFilter,
    snapshotMode,
});

// References arrive as { id } from Aidbox, or as a FHIR "Type/id" reference string.
const refId = (ref) => ref?.id || ref?.reference?.split('/').pop() || null;

const chunk = (list, size) => {
    const chunks = [];
    for (let i = 0; i < list.length; i += size) chunks.push(list.slice(i, i + size));
    return chunks;
};

const fetchResponses = async (qrIds) => {
    const results = await Promise.all(
        chunk(qrIds, ID_CHUNK_SIZE).map(async (ids) => {
            try {
                const response = await $httpFhirApi.get(
                    'QuestionnaireResponse?id=' +
                        ids.join(',') +
                        '&_include=subject,encounter:Encounter&_include=Encounter:episode-of-care&_revinclude=Procedure:encounter:Encounter&_revinclude=Task:focus:QuestionnaireResponse',
                );
                return response?.data || [];
            } catch (error) {
                console.error('Failed to load questionnaire responses for print', ids, error);
                return [];
            }
        }),
    );
    return results.flat();
};

const fetchQuestionnaires = async (canonicals) => {
    const entries = await Promise.all(
        canonicals.map(async (canonical) => {
            try {
                const response = await $httpFhirApi.get(getUrlFromCanonical(canonical).fullUrl);
                return [canonical, response?.data?.[0] || null];
            } catch (error) {
                console.error('Failed to load questionnaire for print', canonical, error);
                return [canonical, null];
            }
        }),
    );
    return new Map(entries);
};

const fetchSigners = async (resourceType, ids, elements) => {
    const results = await Promise.all(
        chunk(ids, ID_CHUNK_SIZE).map(async (part) => {
            try {
                const response = await $httpFhirApi.get(`${resourceType}?id=${part.join(',')}&_elements=${elements}`);
                return response?.data || [];
            } catch (error) {
                console.error(`Failed to load signer ${resourceType} records for print`, part, error);
                return [];
            }
        }),
    );
    return results.flat();
};

// An unreadable chunk (null) prints without designations rather than failing the print.
const fetchRelationships = async (subjectIds) => {
    const ids = [...new Set(subjectIds.filter(Boolean))];
    const results = await Promise.all(chunk(ids, ID_CHUNK_SIZE).map((part) => fetchRelationshipsForPatients(part)));
    return results.flatMap((rows) => rows || []);
};

/**
 * Print data for each `{ qrId, canonical }` entry, in input order. An entry whose response or
 * questionnaire can't be loaded is left out; this never rejects.
 */
export const fetchFormPrintData = async (entries) => {
    const wanted = (entries || []).filter((entry) => entry?.qrId);
    if (!wanted.length) return [];

    const resources = await fetchResponses([...new Set(wanted.map((entry) => entry.qrId))]);
    const ofType = (type) => resources.filter((r) => r?.resourceType === type);
    const responses = ofType('QuestionnaireResponse');
    const patients = ofType('Patient');
    const encounters = ofType('Encounter');
    const episodes = ofType('EpisodeOfCare');
    const procedures = ofType('Procedure');
    // The form's own task; signer tasks also focus the response but complete later, at signing.
    const formTasks = ofType('Task').filter((task) => task.code?.coding?.some((coding) => coding.code === 'form'));

    const loaded = wanted
        .map((entry) => {
            const qr = responses.find((r) => r.id === entry.qrId);
            if (!qr) console.error('Questionnaire response not found for print', entry.qrId);
            return qr ? { qr, canonical: entry.canonical || qr.questionnaire } : null;
        })
        .filter(Boolean);
    if (!loaded.length) return [];

    const loadedResponses = loaded.map(({ qr }) => qr);
    const reviewers = loadedResponses.flatMap((qr) => qr.reviewer || []);
    const practitionerIds = [...new Set(reviewers.map((r) => r.who?.id).filter(Boolean))];
    const patientIds = [
        ...new Set(
            reviewers
                .filter((r) => r.who?.resourceType === 'Patient')
                .map((r) => r.who.id)
                .filter(Boolean),
        ),
    ];

    const [questionnaires, relationships, signerPractitioners, signerPatients] = await Promise.all([
        fetchQuestionnaires([...new Set(loaded.map(({ canonical }) => canonical).filter(Boolean))]),
        fetchRelationships(loadedResponses.map((qr) => refId(qr.subject))),
        fetchSigners('Practitioner', practitionerIds, 'id,name,timeZone,credentials,jobTitle'),
        fetchSigners('Patient', patientIds, 'id,name'),
    ]);
    const signerSources = [...signerPractitioners, ...signerPatients];

    return loaded
        .map(({ qr, canonical }) => {
            const questionnaire = questionnaires.get(canonical);
            if (!questionnaire) {
                console.error('Questionnaire not found for print', canonical, qr.id);
                return null;
            }
            const encounter = encounters.find((e) => e.id === refId(qr.encounter));
            const episodeIds = (encounter?.episodeOfCare || []).map(refId);
            const formTask = formTasks.find((task) => refId(task.focus) === qr.id);
            return buildFormPrintData({
                questionnaire,
                questionnaireResponse: qr,
                person: patients.find((p) => p.id === refId(qr.subject)),
                encounter,
                episodeOfCare: episodes.find((e) => episodeIds.includes(e.id)),
                procedures: encounter ? procedures.filter((p) => refId(p.encounter) === encounter.id) : [],
                relationships,
                signerSources,
                lockTimestamp: qr.status === 'completed' ? formTask?.executionPeriod?.end : null,
            });
        })
        .filter(Boolean);
};
